import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, ShieldCheck, UserCog, UserRoundX } from "lucide-react";
import { toast } from "react-hot-toast";

import EmptyState from "@/components/layout/EmptyState";
import MetricCard from "@/components/layout/MetricCard";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import type { UserProfile, UserRole } from "@/lib/api-types";
import { useAuthStore } from "@/stores/auth";

type UserDraft = {
  role: UserRole;
  isActive: boolean;
};

function formatRoleLabel(role: UserRole): string {
  switch (role) {
    case "admin":
      return "Admin";
    case "moderator":
      return "Moderator";
    default:
      return "User";
  }
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return "Never";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("vi-VN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

export default function UsersPage() {
  const currentUser = useAuthStore((state) => state.user);
  const updateCurrentUser = useAuthStore((state) => state.updateUser);
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<number, UserDraft>>({});

  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: api.getUsers,
  });

  useEffect(() => {
    if (!usersQuery.data) {
      return;
    }

    setDrafts(
      usersQuery.data.reduce<Record<number, UserDraft>>((accumulator, user) => {
        accumulator[user.id] = {
          role: user.role,
          isActive: user.isActive,
        };
        return accumulator;
      }, {}),
    );
  }, [usersQuery.data]);

  const updateUserMutation = useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: Partial<UserDraft> }) =>
      api.updateUser(id, updates),
    onSuccess: (updatedUser) => {
      toast.success(`Đã cập nhật user ${updatedUser.username}`);
      queryClient.setQueryData<UserProfile[]>(["users"], (current) =>
        (current ?? []).map((user) =>
          user.id === updatedUser.id ? updatedUser : user,
        ),
      );
      setDrafts((current) => ({
        ...current,
        [updatedUser.id]: {
          role: updatedUser.role,
          isActive: updatedUser.isActive,
        },
      }));
      if (currentUser?.id === updatedUser.id) {
        updateCurrentUser(updatedUser);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const metrics = useMemo(() => {
    const users = usersQuery.data ?? [];
    return {
      total: users.length,
      active: users.filter((user) => user.isActive).length,
      admins: users.filter((user) => user.role === "admin" && user.isActive).length,
      disabled: users.filter((user) => !user.isActive).length,
    };
  }, [usersQuery.data]);

  if (usersQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading users...</div>;
  }

  if (usersQuery.isError || !usersQuery.data) {
    return <div className="text-sm text-red-500">Không tải được danh sách user.</div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Admin"
        title="User management"
        description="Quản lý role và trạng thái hoạt động của tài khoản. Tài khoản đăng ký đầu tiên sẽ tự động là admin."
        actions={
          <Button
            variant="outline"
            onClick={() => {
              void usersQuery.refetch();
            }}
            disabled={usersQuery.isFetching}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${usersQuery.isFetching ? "animate-spin" : ""}`} />
            Reload
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Total users"
          value={metrics.total.toLocaleString("vi-VN")}
          hint="Tổng số tài khoản đã đăng ký."
          icon={<UserCog className="h-4 w-4" />}
        />
        <MetricCard
          label="Active users"
          value={metrics.active.toLocaleString("vi-VN")}
          hint="Các tài khoản còn đăng nhập và sử dụng được."
          icon={<ShieldCheck className="h-4 w-4" />}
        />
        <MetricCard
          label="Active admins"
          value={metrics.admins.toLocaleString("vi-VN")}
          hint="Cần giữ ít nhất một admin đang hoạt động."
          icon={<ShieldCheck className="h-4 w-4" />}
        />
        <MetricCard
          label="Disabled users"
          value={metrics.disabled.toLocaleString("vi-VN")}
          hint="Các tài khoản đã bị khóa."
          icon={<UserRoundX className="h-4 w-4" />}
        />
      </div>

      <Card className="border-border/80 bg-card/80">
        <CardHeader>
          <CardTitle>Accounts</CardTitle>
          <CardDescription>
            Chỉ admin mới có thể đổi role hoặc khóa tài khoản. Thay đổi được áp dụng ngay sau khi lưu.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {usersQuery.data.length === 0 ? (
            <EmptyState
              title="Chưa có user"
              description="Khi có người đăng ký, danh sách tài khoản sẽ xuất hiện ở đây."
              className="p-6"
            />
          ) : (
            usersQuery.data.map((user) => {
              const draft = drafts[user.id] ?? {
                role: user.role,
                isActive: user.isActive,
              };
              const hasChanges =
                draft.role !== user.role || draft.isActive !== user.isActive;
              const isSaving =
                updateUserMutation.isPending &&
                updateUserMutation.variables?.id === user.id;

              return (
                <div
                  key={user.id}
                  className="rounded-2xl border border-border/80 bg-muted/20 p-4"
                >
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-base font-medium">
                          {user.username}
                        </p>
                        <span className="rounded-full border border-border/80 px-2 py-1 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                          {formatRoleLabel(user.role)}
                        </span>
                        {!user.isActive ? (
                          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] uppercase tracking-[0.16em] text-amber-700">
                            Disabled
                          </span>
                        ) : null}
                        {currentUser?.id === user.id ? (
                          <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-1 text-[11px] uppercase tracking-[0.16em] text-primary">
                            You
                          </span>
                        ) : null}
                      </div>
                      <p className="truncate text-sm text-muted-foreground">
                        {user.email}
                      </p>
                      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                        <span>Created {formatDateTime(user.createdAt)}</span>
                        <span>Last login {formatDateTime(user.lastLogin)}</span>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-[minmax(0,180px)_minmax(0,180px)_auto] xl:min-w-[520px]">
                      <select
                        value={draft.role}
                        onChange={(event) => {
                          const nextRole = event.target.value as UserRole;
                          setDrafts((current) => ({
                            ...current,
                            [user.id]: {
                              role: nextRole,
                              isActive: current[user.id]?.isActive ?? user.isActive,
                            },
                          }));
                        }}
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                        disabled={isSaving}
                      >
                        <option value="admin">Admin</option>
                        <option value="moderator">Moderator</option>
                        <option value="user">User</option>
                      </select>

                      <select
                        value={draft.isActive ? "active" : "inactive"}
                        onChange={(event) => {
                          setDrafts((current) => ({
                            ...current,
                            [user.id]: {
                              role: current[user.id]?.role ?? user.role,
                              isActive: event.target.value === "active",
                            },
                          }));
                        }}
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                        disabled={isSaving}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>

                      <Button
                        onClick={() => {
                          const updates: Partial<UserDraft> = {};
                          if (draft.role !== user.role) {
                            updates.role = draft.role;
                          }
                          if (draft.isActive !== user.isActive) {
                            updates.isActive = draft.isActive;
                          }
                          updateUserMutation.mutate({ id: user.id, updates });
                        }}
                        disabled={!hasChanges || isSaving}
                      >
                        {isSaving ? "Saving..." : "Save"}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
